import React from "react";

import getArticles from "@/api/requests/wordpress/getArticles";
import getAuthor from "@/api/requests/wordpress/getAuthor";
import getMenuItems from "@/api/requests/wordpress/getMenuItems";
import getYoastRedirect from "@/api/requests/wordpress/getYoastRedirect";
import {
  ArticleExcerpt,
  AuthorPage as AuthorPageType,
  MenuItems,
} from "@/custom-types/Wordpress";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import { getUrlPath } from "@/utils/getUrlPath";
import { getCountryCodeHeader } from "@/utils/requestContext";

import AuthorBox from "@/components/news/AuthorBox";
import ListLayout from "@/components/news/Layouts/ListLayout";
import PageLayout from "@/components/news/Layouts/PageLayout";

type AuthorPagePageProps = {
  menuItems: MenuItems;
  author: AuthorPageType;
  articles: ArticleExcerpt[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
};

const AuthorPagePage: React.FC<AuthorPagePageProps> = ({
  author,
  articles,
  menuItems,
  totalCount,
  totalPages,
  currentPage,
}) => {
  const breadcrumbs = [
    {
      href: "/news",
      title: "News",
    },
    {
      href: `/news/author/${author.slug}`,
      title: author.title,
    },
    {
      href: `/news/author/${author.slug}/page/${currentPage}`,
      title: `Page ${currentPage}`,
    },
  ];

  return (
    <PageLayout
      menuItems={menuItems}
      page={{ ...author, breadcrumbs }}
      totalPages={totalPages}
      currentPage={currentPage}
    >
      <AuthorBox
        author={{ ...author, name: author.title }}
        className="mb-xxl"
      />
      <ListLayout
        articles={articles}
        totalCount={totalCount}
        currentPage={currentPage}
        totalPages={totalPages}
        slug={author.slug}
      />
    </PageLayout>
  );
};

export const getServerSideProps = createGetServerSideProps<
  AuthorPagePageProps,
  {
    authorSlug: string;
    page: string;
  }
>(async (context) => {
  const { resolvedUrl, params } = context;

  const redirect = await getYoastRedirect(getUrlPath(resolvedUrl));

  if (redirect?.destination) {
    return {
      redirect,
    };
  }

  if (!params?.authorSlug) {
    return {
      notFound: true,
    };
  }

  if (
    !params?.page ||
    Number.isNaN(Number(params.page)) ||
    params.page === "1"
  ) {
    return {
      redirect: {
        destination: `/news/author/${params.authorSlug}`,
        statusCode: 301,
      },
    };
  }

  const author = await getAuthor(params.authorSlug);

  if (!author) {
    return {
      notFound: true,
    };
  }

  const countryCode = getCountryCodeHeader(context.req.headers);
  const [{ articles, totalCount, totalPages, pageOutOfBounds }, menuItems] =
    await Promise.all([
      getArticles(
        countryCode,
        {
          author: `${author.id}`,
        },
        params.page,
      ),
      getMenuItems(countryCode === "CA" ? "canada" : "global"),
    ]);

  if (pageOutOfBounds) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      articles: articles as ArticleExcerpt[],
      author,
      currentPage: Number(params?.page),
      menuItems,
      totalCount,
      totalPages,
    },
  };
});

export default AuthorPagePage;
